import React, { useState } from 'react'

import { faExternalLink } from '@fortawesome/pro-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { Box, Button, Link, Typography } from '@material-ui/core'
import { makeStyles } from '@material-ui/core/styles'
import PropTypes from 'prop-types'

import api from 'common/api'
import useScid from 'common/use-scid'

import { APP } from '../../../../../common/metadata-constants'
import useCanManageApplications from '../../../useCanManageApplications'
import { APPLICATION_STATUS } from '../../constants'
import AppStatusRefresher from '../AppStatusRefresher'

export const REFRESH_INTERVAL = 30000

const useStyles = makeStyles((theme) => ({
  container: {
    maxWidth: theme.spacing(68),
  },
  statusContainer: {
    width: theme.spacing(64),
    height: theme.spacing(10),
    backgroundColor: '#e7f4f7',
  },
  refreshButton: {
    fontSize: theme.typography.pxToRem(15),
  },
  continueButton: {
    maxWidth: theme.spacing(64),
  },
}))

const TestSubmissionStep = ({ appConfig, refetchAppConfig, completeStep }) => {
  const initiationVerified = appConfig && appConfig.verification && appConfig.verification.initiationVerified
  const appStatus = appConfig && appConfig.verificationAppStatus
  const [verifying, setVerifying] = useState(false)
  const [verificationError, setVerificationError] = useState(false)
  const [ssoError, setSsoError] = useState(false)
  const styles = useStyles()
  const canCompleteStep = useCanManageApplications()
  const scid = useScid()
  const handleSSO = () => {
    const params = new URLSearchParams({ EventSourceApplication: APP })
    const opts = {
      headers: {
        'x-scoir-event-data': params.toString(),
      },
    }

    api
      .get(
        `/api/colleges/${scid}/integrations/applications/${appConfig.verificationAppId}/sso?applicationCycle=${appConfig.applicationCycle}`,
        opts,
      )
      .then((ssoUrl) => {
        if (ssoUrl) {
          window.open(ssoUrl, '_blank')
          setSsoError(false)
        } else {
          setSsoError(true)
        }
      })
      .catch(() => {
        setSsoError(true)
      })
  }
  const verifyAppSubmission = () => {
    setVerifying(true)
    api
      .put(
        `/api/colleges/${scid}/integrations/application/configuration/${appConfig.id}/verification/applicationSubmission`,
      )
      .then(async () => {
        setVerificationError(false)
        completeStep()
      })
      .catch(() => {
        setVerifying(false)
        setVerificationError(true)
      })
  }

  return (
    <Box
      px={4}
      py={1}
    >
      <Typography>
        {
          "Click on the link below to imitate an applicant and single sign-on into your institution's supplement. Then, complete and submit the supplement."
        }
      </Typography>
      <Box my={3}>
        <Button
          color="primary"
          variant="contained"
          onClick={handleSSO}
          disabled={!initiationVerified || !canCompleteStep}
        >
          <FontAwesomeIcon icon={faExternalLink} />
          &nbsp; Open Supplement in New Tab
        </Button>
        {ssoError && (
          <Box mt={2}>
            <Typography
              variant="caption"
              color="error"
            >
              An error occured while navigating to your application.{' '}
              <Link
                href="https://www.scoir.com/support"
                target="_blank"
                rel="noreferrer"
              >
                Please contact customer support.
              </Link>
            </Typography>
          </Box>
        )}
      </Box>
      <hr />
      <Box
        display="flex"
        flexDirection="column"
        mt={3}
        className={styles.container}
      >
        <Typography>
          After completion, confirm the status below updates to <strong>Submitted</strong>.
        </Typography>
        <Typography
          variant="caption"
          color="textSecondary"
        >
          If the status does not change after refreshing please{' '}
          <Link
            href="https://www.scoir.com/support"
            target="_blank"
          >
            Contact Scoir Support.
          </Link>
        </Typography>
        <AppStatusRefresher
          status={appConfig && appConfig.verificationAppStatus}
          refresh={refetchAppConfig}
          shouldRefresh={appStatus === APPLICATION_STATUS.Initiated && canCompleteStep}
          refreshInterval={REFRESH_INTERVAL}
        />
        <Button
          color="primary"
          variant="contained"
          className={styles.continueButton}
          disabled={appStatus !== APPLICATION_STATUS.Submitted || !canCompleteStep || verifying}
          onClick={verifyAppSubmission}
        >
          Verify and Continue
        </Button>
        {verificationError && (
          <Box mt={2}>
            <Typography
              variant="caption"
              color="error"
            >
              An error occured while verifying your application.{' '}
              <Link
                href="https://www.scoir.com/support"
                target="_blank"
                rel="noreferrer"
              >
                Please contact customer support.
              </Link>
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  )
}

TestSubmissionStep.propTypes = {
  appConfig: PropTypes.object,
  refetchAppConfig: PropTypes.func.isRequired,
  completeStep: PropTypes.func,
}

export default TestSubmissionStep
